import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'

import {
  getAmenities,
  selectAmenities,
  toggleDashboard,
} from '../../../reducers/Organisation/reservationsReducer'
import { getNamedHandler, getTimeBlockEndTime } from '../helpers'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import ModalContainer from '../../ModalContainer/ModelContainer'
import Icon from '../../Icons'
import Stepper from '../../../components/Stepper/Stepper'
import api from '../../../api/api'

import SelectAmenity from './Steps/SelectAmenity'
import SelectLocation from './Steps/SelectLocation'
import SelectDateAndTime from './Steps/SelectDateAndTime'
import TimeBlockSummary from './Steps/TimeBlockSummary'
import ConflictReservations from './Steps/ConflictReservations'

const ReservationsAddTimeBlockModal = ({
  buildingData,
  amenities,
  onClose = () => {},
  getAmenities = () => {},
  toggleDashboard = () => {},
}) => {
  const { t } = useTranslation(['reservations'])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingAmenities, setIsLoadingAmenities] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [hasConflicts, setHasConflicts] = useState(false)
  const [reservedTimeRanges, setReservedTimeRanges] = useState([])
  const [addNotes, setAddNotes] = useState(false)
  const [formState, setFormState] = useState({
    amenity: null,
    location: null,
    date: null,
    endDate: null,
    startTime: null,
    endTime: null,
    noteTitle: null,
    noteBody: null,
    blockMultipleDays: false,
    blockFullDay: false,
    reason: 'General',
    reasonOther: null,
    description: null,
  })
  const { displaySnackbar } = useSnackbar()

  const handleFieldChange = ({ target: { name, value } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const fetchAmenities = async () => {
    setIsLoadingAmenities(true)
    try {
      await getAmenities(buildingData.id)
    } catch ({ message }) {
      console.error(message)
    }
    setIsLoadingAmenities(false)
  }

  useEffect(() => {
    if (buildingData) {
      fetchAmenities()
    }
  }, [buildingData])

  const handleNamedField = getNamedHandler(handleFieldChange)

  const handleSelectAmenity = (amenity) => handleNamedField(amenity, 'amenity')
  const handleSelectLocation = (location) => handleNamedField(location, 'location')

  useEffect(() => {
    setFormState((prev) => ({ ...prev, location: null }))
  }, [formState.amenity])

  useEffect(() => {
    if (formState.reason !== 'Other') {
      setFormState((prev) => ({ ...prev, reasonOther: null }))
    }
  }, [formState.reason])

  const onCalendarChange = (item) => {
    const { dateRange = [], timeRange = [], blockMultipleDays = false, blockFullDay } = item
    const [startDate, endDate] = dateRange
    const [startTime, endTime] = timeRange

    setFormState((prev) => ({
      ...prev,
      date: startDate,
      endDate,
      startTime,
      endTime,
      blockMultipleDays,
      blockFullDay,
    }))
  }

  const onSubmit = async () => {
    setIsLoading(true)
    try {
      const requestData = {
        amenityId: formState.amenity.id,
        locationId: formState.location ? formState.location.id : null,
        date: formState.date,
        endDate: formState.endDate ? formState.endDate : null,
        startTime: formState.startTime,
        endTime:
          typeof formState.startTime === 'number'
            ? getTimeBlockEndTime(
                formState.startTime,
                formState.endTime,
                formState.amenity.timeInterval,
              )
            : null,
        blockEntireDay: formState.blockFullDay,
        noteTitle: formState.noteTitle,
        noteBody: formState.noteBody,
        reason: formState.reason,
        reasonOther: formState.reason === 'Other' ? formState.reasonOther?.trim() || null : null,
        description: formState.description?.trim() || null,
      }
      await api.createTimeBlock(requestData)
      setIsLoading(false)
      toggleDashboard()
      displaySnackbar(t('reservations:reservationsAddTimeBlockModal.timeBlockCreated'))
      onClose()
    } catch ({ message, response }) {
      console.error(message)
      displaySnackbar((response.data && response.data.message) || message, true)
      setIsLoading(false)
    }
  }

  const availablePlaces = formState?.amenity?.locations || []

  const isAmenity = !!formState.amenity
  const hasPlaces = availablePlaces?.length > 0

  const selectDateAndTimeButtonEnabled =
    (isAmenity &&
      (formState.amenity.multiDayBooking || formState.blockMultipleDays) &&
      (!!formState.date || !!formState.endDate)) ||
    typeof formState.startTime === 'number'

  const isOtherReasonMissing = formState.reason === 'Other' && !formState.reasonOther?.trim()
  const summaryButtonEnabled =
    !isOtherReasonMissing && (!addNotes || (addNotes && formState.noteTitle && formState.noteBody))

  const steps = useMemo(() => {
    const items = [
      {
        Component: SelectAmenity,
        title: t('reservations:reservationForClientForm.stepperSteps.selectAmenity'),
        showStepper: true,
        config: {
          canGoNext: isAmenity,
          amenities,
          selectedAmenity: formState.amenity,
          handleSelectAmenity,
          isLoadingAmenities,
        },
      },
      ...(hasPlaces
        ? [
            {
              Component: SelectLocation,
              title: t('reservations:reservationForClientForm.stepperSteps.selectPlace'),
              showStepper: true,
              config: {
                canGoNext: !!formState.location,
                selectedLocation: formState.location,
                availablePlaces,
                handleSelectLocation,
              },
            },
          ]
        : []),
      {
        Component: SelectDateAndTime,
        title:
          (formState.amenity && formState.amenity.multiDayBooking) || formState.blockMultipleDays
            ? t('reservations:reservationForClientForm.stepperSteps.selectDates')
            : t('reservations:reservationForClientForm.stepperSteps.selectDate'),
        showStepper: true,
        config: {
          canGoNext: selectDateAndTimeButtonEnabled,
          formState,
          buildingData,
          hasConflicts,
          onCalendarChange,
          setHasConflicts,
          setReservedTimeRanges,
        },
      },
      {
        Component: TimeBlockSummary,
        title: t('reservations:reservationsAddTimeBlockModal.timeBlockSummaryTitle'),
        stepLabel: t('reservations:reservationsAddTimeBlockModal.timeBlockSummaryStepLabel'),
        showStepper: true,
        config: {
          timezone: buildingData?.timezone,
          canGoNext: !!summaryButtonEnabled,
          selectedLocation: formState.location,
          formState,
          addNotes,
          setAddNotes,
          handleFieldChange,
          onSubmit,
        },
      },
    ]

    if (hasConflicts) {
      const dateStepIndex = items.findIndex((i) => i.Component === SelectDateAndTime)
      const itemsBeforeConflict = items.slice(0, dateStepIndex + 1)
      const itemsAfterConflict = items.slice(dateStepIndex + 1)

      return [
        ...itemsBeforeConflict,
        ...[
          {
            Component: ConflictReservations,
            title: t('reservations:reservationsAddTimeBlockModal.conflictingReservationsTitle'),
            showStepper: false,
            config: {
              formState,
              buildingData,
              reservedTimeRanges,
            },
          },
        ],
        ...itemsAfterConflict,
      ]
    }

    return items
  }, [
    isAmenity,
    hasPlaces,
    selectDateAndTimeButtonEnabled,
    hasConflicts,
    formState,
    buildingData,
    amenities,
    addNotes,
    isLoadingAmenities,
    reservedTimeRanges,
    handleSelectAmenity,
    handleSelectLocation,
  ])

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1)
  }

  const handleBack = () => {
    if (!currentStep) {
      return onClose()
    }
    setCurrentStep((prev) => prev - 1)
  }

  const renderStep = useCallback(() => {
    const { Component, config, title, showStepper } = steps[currentStep]

    return (
      <div>
        {showStepper && (
          <Stepper
            currentStep={currentStep}
            steps={steps.filter((element) => element.showStepper).map((step) => step.title)}
          />
        )}
        <Component
          onNext={handleNext}
          onBack={handleBack}
          title={title}
          style={{ paddingTop: showStepper ? 0 : 20 }}
          {...config}
        />
      </div>
    )
  }, [steps])

  return (
    <ModalContainer
      headerTitle={t('reservations:reservationsAddTimeBlockModal.headerTitle')}
      additionalClass={`reservations-modal modal-container--extended-zindex ${isLoading ? 'is-loading' : ''}`}
      headerIcon={
        <Icon
          icon="clockClose"
          fill="none"
          svgProps={{
            stroke: 'black',
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
          }}
        />
      }
      onClose={onClose}
    >
      {renderStep()}
    </ModalContainer>
  )
}

ReservationsAddTimeBlockModal.propTypes = {
  onClose: PropTypes.func,
  getAmenities: PropTypes.func,
  toggleDashboard: PropTypes.func,
  buildingData: PropTypes.object,
  amenities: PropTypes.array,
}

export default connect(
  (state) => ({
    amenities: selectAmenities(state),
  }),
  {
    getAmenities,
    toggleDashboard,
  },
)(ReservationsAddTimeBlockModal)
